import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/axios';
import { BellIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import {
    CheckCircleIcon,
    XCircleIcon,
    InformationCircleIcon,
    XMarkIcon
} from '@heroicons/react/24/solid';

const NotificationBell = () => {
    const [notifications, setNotifications] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef(null);

    // 1. Fetch Notifications (Expects History from Backend)
    const fetchNotifications = useCallback(async (force = false) => {
        if (loading) return;
        if (force) setLoading(true);

        try {
            const res = await api.get('/leaves/notifications');
            const data = res.data; // Should contain both Read and Unread

            // Calculate Red Dot count (Only Unread items)
            const unread = data.filter(n => !n.is_read).length;
            setUnreadCount(unread);

            // Update the list if menu is closed OR forced refresh
            if (force || !isOpen) {
                setNotifications(data);
            }
        } catch (err) {
            console.error("Notify Error");
        } finally {
            if (force) setLoading(false);
        }
    }, [isOpen, loading]);

    // 2. Poll every 15 seconds
    useEffect(() => {
        fetchNotifications(false);
        const interval = setInterval(() => {
            fetchNotifications(false);
        }, 15000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    // 3. Toggle Menu
    const handleToggle = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            fetchNotifications(true);
        }
    };

    // 4. Click Outside to Close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // 5. MARK AS READ (Clicking the Row)
    // Changes style, Decreases Badge, KEEPS in list
    const handleMarkAsRead = async (e, id) => {
        e.stopPropagation();

        const item = notifications.find(n => n.id === id);
        if (item && item.is_read) return; // Already read, ignore

        // Optimistic UI Update: Set is_read = true
        setNotifications(prev => prev.map(n =>
            n.id === id ? { ...n, is_read: true } : n
        ));

        // Decrease Badge
        setUnreadCount(prev => Math.max(0, prev - 1));

        // API Call
        try {
            await api.put(`/leaves/notifications/${id}`);
        } catch (err) { }
    };

    // 6. DELETE (Clicking 'X')
    // Removes from list entirely
    const handleDelete = async (e, id) => {
        e.stopPropagation();
        try {
            // Assuming your backend supports DELETE or you use the same mark-read endpoint
            // If you don't have a delete endpoint, just hide it from UI:
            setNotifications(prev => prev.filter(n => n.id !== id));

            // If it was unread, lower the count
            const item = notifications.find(n => n.id === id);
            if (item && !item.is_read) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }

            // Optional: Call API if exists
            // await api.delete(`/leaves/notifications/${id}`);
        } catch (err) { }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'success': return <CheckCircleIcon className="h-5 w-5 text-emerald-500" />;
            case 'error': return <XCircleIcon className="h-5 w-5 text-rose-500" />;
            case 'warning': return <InformationCircleIcon className="h-5 w-5 text-amber-500" />;
            default: return <InformationCircleIcon className="h-5 w-5 text-indigo-500" />;
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={handleToggle}
                className="relative p-2 text-slate-500 hover:text-indigo-600 transition-colors rounded-full hover:bg-slate-100 focus:outline-none"
            >
                <BellIcon className="h-6 w-6" />
                {unreadCount > 0 && (
                    <span className="absolute top-1.5 right-2 h-2.5 w-2.5 bg-rose-500 rounded-full ring-2 ring-white animate-pulse"></span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-3 w-80 bg-white rounded-2xl shadow-xl ring-1 ring-slate-900/5 overflow-hidden z-50 animate-fade-in-up origin-top-right">
                    <div className="px-4 py-3 border-b border-slate-50 bg-slate-50/80 backdrop-blur-sm flex justify-between items-center">
                        <h3 className="font-bold text-slate-700 text-sm">Notifications</h3>
                        <div className="flex items-center gap-2">
                            {unreadCount > 0 ? (
                                <span className="text-[10px] bg-rose-100 text-rose-600 px-2 py-0.5 rounded-full font-bold">
                                    {unreadCount} New
                                </span>
                            ) : (
                                <span className="text-[10px] text-slate-400 font-bold">History</span>
                            )}
                            <button
                                onClick={(e) => { e.stopPropagation(); fetchNotifications(true); }}
                                className={`p-1 rounded-full hover:bg-slate-200 transition-all ${loading ? 'animate-spin text-indigo-500' : 'text-slate-400'}`}
                                title="Refresh"
                            >
                                <ArrowPathIcon className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    <div className="max-h-96 overflow-y-auto custom-scrollbar bg-white">
                        {notifications.length === 0 ? (
                            <div className="p-8 text-center flex flex-col items-center justify-center text-slate-400">
                                <BellIcon className="h-8 w-8 mb-2 opacity-20" />
                                <span className="text-sm">No notifications</span>
                            </div>
                        ) : (
                            notifications.map(n => (
                                <div
                                    key={n.id}
                                    onClick={(e) => handleMarkAsRead(e, n.id)}
                                    // Visual Logic: 
                                    // Unread = Blue background, Dark text. 
                                    // Read = White background, Lighter text.
                                    className={`group px-4 py-3.5 border-b border-slate-50 flex items-start transition-all cursor-pointer relative
                                        ${n.is_read
                                            ? 'bg-white opacity-70 hover:opacity-100'
                                            : 'bg-indigo-50/60 hover:bg-indigo-50'
                                        }`}
                                >
                                    {/* Blue Bar for Unread */}
                                    {!n.is_read && (
                                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500"></div>
                                    )}

                                    <div className={`mt-0.5 flex-shrink-0 ${n.is_read ? 'grayscale opacity-50' : ''}`}>
                                        {getIcon(n.type)}
                                    </div>
                                    <div className="ml-3 w-full relative pr-4">
                                        <p className={`text-sm leading-snug ${n.is_read ? 'font-medium text-slate-500' : 'font-bold text-slate-800'}`}>
                                            {n.message}
                                        </p>
                                        <p className="text-[10px] text-slate-400 mt-1.5 font-medium uppercase tracking-wide">
                                            {new Date(n.created_at).toLocaleDateString()} • {new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </p>

                                        {/* Dismiss/Delete Button */}
                                        <button
                                            onClick={(e) => handleDelete(e, n.id)}
                                            className="absolute -top-1 -right-2 p-1 text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                            title="Remove"
                                        >
                                            <XMarkIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    <div className="bg-slate-50 p-2 text-center text-[10px] text-slate-400 uppercase tracking-widest font-bold">
                        SmartBreak Alerts
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;